import { Expose, Type } from "class-transformer";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ja";
import Product from "domain/model/Product";
dayjs.extend(relativeTime);
dayjs.locale("ja");

export const orderStatuses = {
  new: "新規受注",
  processing: "受注承諾",
  completed: "完了",
  cancel: "キャンセル済み",
};

export type OrderStatus = keyof typeof orderStatuses;
export type PaymentType = "store" | "card" | "cod";

/**
 * 適用された割引ルールの詳細
 */
export class AppliedDiscount {
  @Expose() type: "percent" | "amount" | "free_shipping";
  @Expose({ name: "rule_value" }) ruleValue?: number;
  @Expose({ name: "applied_amount" }) appliedAmount?: number;
}

/**
 * 割引情報
 */
export class Discount {
  @Expose({ name: "discount_total" }) discountTotal: number;
  @Type(() => AppliedDiscount)
  @Expose({ name: "applied_discounts" })
  appliedDiscounts: AppliedDiscount[];
  @Expose({ name: "free_shipping" }) freeShipping: boolean;
  @Expose({ name: "shipping_discount_amount" })
  shippingDiscountAmount: number;
}

/**
 * 注文者情報が全て揃っている Order の型
 * hasOptionalOrdererInfo() の型ガードで絞り込まれた後に使用
 */
export interface OrderWithOrdererInfo extends Order {
  ordererName: string;
  ordererTel: string;
  ordererZipCode: string;
  ordererAddress: string;
}

export default class Order {
  @Expose() id: number;
  @Expose({ name: "order_number" }) orderNumber: string;
  @Expose({ name: "order_status" }) orderStatus: OrderStatus;
  @Expose({ name: "order_price" }) orderPrice: number;
  @Expose({ name: "ordered_at" }) orderedAt: string;
  @Expose({ name: "completed_at" }) completedAt: string;
  @Expose({ name: "payment_type" }) paymentType: PaymentType;
  @Expose({ name: "settlement" }) settlement: Boolean;
  @Expose() email: string;
  @Expose({ name: "taker_name" }) name: string;
  @Expose({ name: "takeout_date" }) takeoutDate: string;
  @Expose({ name: "takeout_time" }) takeoutTime: string;
  @Expose() tel: string;
  @Expose({ name: "shipping_zip_code" }) shippingZipCode: string | null;
  @Expose({ name: "shipping_address" }) shippingAddress: string | null;
  @Expose({ name: "shipping_fee" }) shippingFee: number | null;
  @Expose() remarks: string;

  // 「配送先を別に設定する」ON時の注文者情報（nullable）
  @Expose({ name: "orderer_name" }) ordererName: string | null;
  @Expose({ name: "orderer_tel" }) ordererTel: string | null;
  @Expose({ name: "orderer_zip_code" }) ordererZipCode: string | null;
  @Expose({ name: "orderer_address" }) ordererAddress: string | null;

  // 割引情報（nullable: 過去の注文では null の可能性がある）
  @Type(() => Discount)
  @Expose()
  discount: Discount | null;

  @Expose({ name: "order_details" })
  @Type(() => OrderDetail)
  orderDetails: OrderDetail[];

  hasShippingInformation(): boolean {
    return this.shippingZipCode != null && this.shippingAddress != null;
  }

  /**
   * オプショナルな注文者情報（orderer_*）が全て設定されているかどうか（型ガード）
   *
   * 【データモデルとUXのねじれ】
   * DBでは配送先(taker_name等)が必須、注文者(orderer_*)がオプショナル。
   * しかしユーザー画面では「配送先を別に設定する」というUIで、
   * あたかも配送先がオプショナルかのように見せている。
   *
   * 【アトミック性】
   * フロント側バリデーションで4フィールド全て必須としているため、
   * 1つでもあれば全てあるはず。型ガードで絞り込むことで型安全に扱える。
   */
  hasOptionalOrdererInfo(): this is OrderWithOrdererInfo {
    return !!(
      this.ordererName &&
      this.ordererTel &&
      this.ordererZipCode &&
      this.ordererAddress
    );
  }

  /**
   * 表示用の注文者名（注文者情報があれば orderer_name、なければ taker_name）
   */
  get displayOrdererName(): string {
    // 型ガードにより ordererName は string に絞り込まれる
    return this.hasOptionalOrdererInfo() ? this.ordererName : this.name;
  }

  /**
   * 表示用の注文者電話番号（注文者情報があれば orderer_tel、なければ tel）
   */
  get displayOrdererTel(): string {
    return this.hasOptionalOrdererInfo() ? this.ordererTel : this.tel;
  }

  /**
   * 表示用の注文者郵便番号（注文者情報があれば orderer_zip_code、なければ shipping_zip_code）
   */
  get displayOrdererZipCode(): string | null {
    return this.hasOptionalOrdererInfo()
      ? this.ordererZipCode
      : this.shippingZipCode;
  }

  /**
   * 表示用の注文者住所（注文者情報があれば orderer_address、なければ shipping_address）
   */
  get displayOrdererAddress(): string | null {
    return this.hasOptionalOrdererInfo()
      ? this.ordererAddress
      : this.shippingAddress;
  }

  /**
   * 受注日時の現在時刻からの差分時間を表示する
   */
  get orderDateTimeFromNow() {
    return dayjs(this.orderedAt).fromNow();
  }

  /**
   * 受注日時を 06:00〜29:30 の時刻範囲で返す（YYYY年MM月DD日 HH:mm）
   */
  get orderDateTimeRenderValue() {
    const datetime = dayjs(this.orderedAt);
    return this.getDateTimeRenderStr(datetime);
  }

  /**
   * 受け渡し時刻の現在時刻からの差分時間を表示する
   */
  get takeoutDateTimeFromNow() {
    // 簡易 EC の場合、受け渡し日時は現状未使用なので空文字を返す
    if (!this.takeoutTime) return "";

    const datetime = dayjs(`${this.takeoutDate} ${this.takeoutTime}`);
    return datetime.fromNow();
  }

  /**
   * 受け渡し日時を 06:00〜29:30 の時刻範囲で返す（YYYY年MM月DD日 HH:mm）
   */
  get takeoutDateTimeRendeValue() {
    // 簡易 EC の場合、受け渡し日時は現状未使用なので空文字を返す
    if (!this.takeoutTime) return "";

    // takeoutTime は最初から 06:00〜29:30 の範囲で設定されている
    const datetime = dayjs(`${this.takeoutDate} ${this.takeoutTime}`);
    return this.getDateTimeRenderStr(datetime);
  }

  /**
   * 時刻が 00:**〜05:** の場合、1日前の 24:**〜29:** に変換して YYYY年MM月DD日 HH:mm 形式で日時を返す
   * keepDate:
   * @param datetime Dayjs のインスタンス
   * @param keepDate もともと日付が正しい場合、これを true とすると日付は維持し、時刻のみ 00:00 → 24:00 のように変換する
   * @param dateFormat 日付フォーマットを指定（なお時刻フォーマットは固定）
   */
  private getDateTimeRenderStr(datetime: Dayjs, dateFormat = "YYYY年MM月DD日") {
    const timeFormat = "HH:mm";
    const h = datetime.hour();
    if (6 <= h) {
      return datetime.format(`${dateFormat} ${timeFormat}`);
    } else {
      const dateStr = datetime.subtract(1, "day").format(dateFormat);
      // 愚直な実装だが手っ取り早くこうする
      const timeStr = datetime
        .format(timeFormat)
        .replace("00:", "24:")
        .replace("01:", "25:")
        .replace("02:", "26:")
        .replace("03:", "27:")
        .replace("04:", "28:")
        .replace("05:", "29:");
      return `${dateStr} ${timeStr}`;
    }
  }
}

export class OrderDetail {
  @Expose()
  @Type(() => Product)
  product: Product;
  @Expose() number: number;
  @Expose({ name: "unit_price" }) unitPrice: number;
  @Expose({ name: "unit_tax_rate" }) unitTaxRate: number;
  @Expose({ name: "tax_rate_reduced" }) taxRateReduced: boolean;
  @Expose({ name: "options" }) options: string;
}

export const makeStatusLabel = (orderStatus: OrderStatus) =>
  orderStatuses[orderStatus];

export const makePaymentTypeLabel = (
  paymentType: PaymentType,
  settlement: Boolean
) => {
  switch (paymentType) {
    case "store":
      return "店舗で決済";
    case "card":
      return `クレジットカード${settlement ? "(支払い済)" : "(未決済)"}`;
    case "cod":
      return `代引き決済`;
  }
};
